import React, { useState, useEffect } from 'react';
import apiDefinitions from "../../../../core/api/apiDefinitions";
import { showSuccessAlert, showDeleteConfirmation, showItemExistsError, showItemAlreadyUsed } from '../../../../core/alert/sweetAlertConfig';

interface FormErrors {
  vehicleFuelTypeName: string;
  selectedVehicleFuelType: string;
}

interface VehicleFuelType {
  id: number;
  fuelTypeName: string;
}

interface VehicleFuelTypeData {
  status: string;
  data: VehicleFuelType[];
  statusDescription?: string;
}

const AddVehicleFuelType: React.FC = () => {
  const [vehicleFuelTypeData, setVehicleFuelTypeData] = useState<VehicleFuelTypeData | null>(null);
  const [isVehicleFuelTypeLoading, setIsVehicleFuelTypeLoading] = useState<boolean>(true);
  const [selectedVehicleFuelTypeId, setSelectedVehicleFuelTypeId] = useState<string>("");
  const [newVehicleFuelTypeName, setNewVehicleFuelTypeName] = useState<string>("");
  const [formErrors, setFormErrors] = useState<FormErrors>({
    vehicleFuelTypeName: "",
    selectedVehicleFuelType: "",
  });
  const [openFail, setOpenFail] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("Operation Failed");

  const handleCloseFail = () => setOpenFail(false);

  // Fetch vehicle fuel types on component mount
  useEffect(() => {
    fetchVehicleFuelTypes();
  }, []);

  const fetchVehicleFuelTypes = () => {
    setIsVehicleFuelTypeLoading(true);
    apiDefinitions.getAllVehicleFuelTypes()
      .then((response: { data: VehicleFuelTypeData }) => {
        if (response.data.status === "S1000" && response.data.data) {
          setVehicleFuelTypeData(response.data);
        } else {
          setErrorMessage(response.data.statusDescription || "Failed to load vehicle fuel types");
          setOpenFail(true);
        }
        setIsVehicleFuelTypeLoading(false);
      })
      .catch((error: any) => {
        setErrorMessage(error.response?.data?.statusDescription || "Failed to load vehicle fuel types: Server error");
        setOpenFail(true);
        setIsVehicleFuelTypeLoading(false);
      });
  };

  const validateVehicleFuelTypeForm = (name: string): boolean => {
    const errors: FormErrors = { vehicleFuelTypeName: "", selectedVehicleFuelType: "" };
    let isValid = true;

    if (!name.trim()) {
      errors.vehicleFuelTypeName = "Vehicle fuel type name is required";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const validateSelectedVehicleFuelType = (): boolean => {
    const errors: FormErrors = { vehicleFuelTypeName: "", selectedVehicleFuelType: "" };
    let isValid = true;

    if (!selectedVehicleFuelTypeId) {
      errors.selectedVehicleFuelType = "Please select a vehicle fuel type";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleAddVehicleFuelTypeSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateVehicleFuelTypeForm(newVehicleFuelTypeName)) return;

    apiDefinitions.createVehicleFuelType({ fuelTypeName: newVehicleFuelTypeName })
      .then((response: { data: VehicleFuelTypeData }) => {
        if (response.data.status === "S1000") {
          showSuccessAlert("Vehicle fuel type added successfully");
          setNewVehicleFuelTypeName("");
          fetchVehicleFuelTypes();
        } else if (response.data.statusDescription?.toLowerCase().includes("already exists")) {
          showItemExistsError("A vehicle fuel type with this name already exists.");
        } else {
          setErrorMessage(response.data.statusDescription || "Vehicle fuel type creation failed");
          setOpenFail(true);
        }
      })
      .catch((error: any) => {
        if (error.response?.data?.statusDescription?.toLowerCase().includes("already exists")) {
          showItemExistsError("A vehicle fuel type with this name already exists.");
        } else {
          setErrorMessage(error.response?.data?.statusDescription || "Failed to create vehicle fuel type: Server error");
          setOpenFail(true);
        }
      });
  };

  const handleDeleteVehicleFuelType = () => {
    if (!validateSelectedVehicleFuelType()) return;

    showDeleteConfirmation(() => {
      apiDefinitions.deleteVehicleFuelType(parseInt(selectedVehicleFuelTypeId))
        .then((response: { data: VehicleFuelTypeData }) => {
          if (response.data.status === "S1000") {
            setSelectedVehicleFuelTypeId("");
            fetchVehicleFuelTypes();
          } else if (response.data.statusDescription?.toLowerCase().includes("already used")) {
            showItemAlreadyUsed("Sorry, this fuel type is already in use and cannot be deleted.");
          } else {
            showItemAlreadyUsed("Sorry, this fuel type is already in use and cannot be deleted.");
          }
        })
        .catch((error: any) => {
          if (error.response?.data?.statusDescription?.toLowerCase().includes("already used")) {
            showItemAlreadyUsed("Sorry, this fuel type is already in use and cannot be deleted.");
          } else {
           showItemAlreadyUsed("Sorry, this fuel type is already in use and cannot be deleted.");
          }
        });
    });
  };

  const handleVehicleFuelTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedVehicleFuelTypeId(e.target.value);
    setFormErrors((prev) => ({ ...prev, selectedVehicleFuelType: "" }));
  };

  return (
    <>
      <div className="card">
        <div className="card-body pb-0">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h4 className="fs-20 mb-0">Add Vehicle Fuel Type</h4>
          </div>
          <div className="row">
            <div className="col-md-12">
              <div className="row" style={{ gap: '30px' }}>
                <div className="col-md-5">
                  <div className="mb-4">
                    <label className="col-form-label mb-2"><h5>Vehicle Fuel Type</h5></label>
                    <div className="d-flex align-items-center">
                      <input
                        type="text"
                        className="form-control"
                        style={{ width: '300px', padding: '10px 15px' }}
                        value={newVehicleFuelTypeName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewVehicleFuelTypeName(e.target.value)}
                        placeholder="Enter vehicle fuel type name"
                      />
                      <button
                        type="button"
                        className="btn btn-success ms-3"
                        data-bs-toggle="modal"
                        data-bs-target="#vehicle-fuel-type-modal"
                        style={{ padding: '10px 12px', whiteSpace: 'nowrap', display: 'inline-block' }}
                      >
                        Add Fuel Type
                      </button>
                    </div>
                    {formErrors.vehicleFuelTypeName && (
                      <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.vehicleFuelTypeName}</div>
                    )}
                    <select
                      className="form-select mt-3"
                      value={selectedVehicleFuelTypeId}
                      onChange={handleVehicleFuelTypeChange}
                      style={{ width: '300px', padding: '10px' }}
                    >
                      <option value="">Select Vehicle Fuel Type</option>
                      {isVehicleFuelTypeLoading ? (
                        <option disabled>Loading...</option>
                      ) : (
                        vehicleFuelTypeData?.data?.map((vehicleFuelType: VehicleFuelType) => (
                          <option key={vehicleFuelType.id} value={vehicleFuelType.id}>{vehicleFuelType.fuelTypeName}</option>
                        ))
                      )}
                    </select>
                    {formErrors.selectedVehicleFuelType && (
                      <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.selectedVehicleFuelType}</div>
                    )}
                    <div className="d-flex gap-2 mt-3">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={handleDeleteVehicleFuelType}
                        style={{ padding: '8px 16px' }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Vehicle Fuel Type Modal */}
      <div className="modal fade" id="vehicle-fuel-type-modal" tabIndex={-1} aria-labelledby="add-vehicle-fuel-type" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="add-vehicle-fuel-type">Add New Vehicle Fuel Type</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleAddVehicleFuelTypeSubmit}>
                <div className="mb-3">
                  <label htmlFor="newVehicleFuelTypeName" className="form-label">Vehicle Fuel Type Name</label>
                  <input
                    type="text"
                    className="form-control"
                    id="newVehicleFuelTypeName"
                    value={newVehicleFuelTypeName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewVehicleFuelTypeName(e.target.value)}
                    placeholder="Enter vehicle fuel type name (e.g., Petrol)"
                    style={{ width: '100%', padding: '10px 15px' }}
                  />
                  {formErrors.vehicleFuelTypeName && (
                    <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.vehicleFuelTypeName}</div>
                  )}
                </div>
                <button type="submit" className="btn btn-success" data-bs-dismiss="modal" style={{ padding: '10px 20px' }}>
                  Add Vehicle Fuel Type
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Error Notification */}
      {openFail && (
        <div
          className="alert alert-danger alert-dismissible fade show"
          role="alert"
          style={{ position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 1100 }}
        >
          {errorMessage}
          <button
            type="button"
            className="btn-close"
            onClick={handleCloseFail}
            aria-label="Close"
          ></button>
        </div>
      )}
    </>
  );
};

export default AddVehicleFuelType;